import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Award, Crosshair, Landmark, Search as SearchIcon, Users } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, EventTypeChip, Loading, Select, StatCard, statusTone } from "../../components/ui";
import { useApi } from "../../api/client";
import { usePortalLink } from "../../utils/portalLink";
import { rowClick } from "../../utils/rowClick";
import { usePageView } from "../../utils/track";

/*
 * The competitions themselves.
 *
 * A competition is what a shooter enters. At Blue Mountains they shot the
 * Bronger on the Saturday or the Carrington on the Sunday — one of the two, not
 * "the meeting" — so the thing they actually competed in needs a list of its
 * own, rather than existing only inside the event that happens to contain it.
 *
 * The event is the container: who hosted it, where, and when. The competition is
 * what was won.
 */
interface CompetitionRow {
  id: string;
  cid: string | null;
  name: string;
  eventId: string;
  eventName: string;
  eid: string | null;
  eventLevel: string;
  eventType: string;
  range: string;
  stateCode: string;
  country: string;
  status: string;
  startDateISO: string | null;
  endDateISO: string | null;
  matches: number;
  shooters: number;
  clubs: number;
  strings: number;
  shots: number;
}

const dateLabel = (iso: string | null) =>
  iso
    ? new Date(iso + "T00:00:00Z").toLocaleDateString("en-AU", {
        timeZone: "UTC",
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function Competitions() {
  usePageView("competitions");
  const link = usePortalLink();
  const navigate = useNavigate();
  const { data: rows, loading } = useApi<CompetitionRow[]>("/competition-list", []);

  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("All States");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [statusFilter, setStatusFilter] = useState("All Status");

  // Options come from the data, so a value a club's Event sheet uses is filterable.
  const states = useMemo(() => Array.from(new Set(rows.map((r) => r.stateCode).filter(Boolean))).sort(), [rows]);
  const types = useMemo(() => Array.from(new Set(rows.map((r) => r.eventType).filter(Boolean))).sort(), [rows]);

  const q = search.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      (!q ||
        [r.name, r.eventName, r.cid ?? "", r.eid ?? "", r.range].join(" ").toLowerCase().includes(q)) &&
      (stateFilter === "All States" || r.stateCode === stateFilter) &&
      (typeFilter === "All Types" || r.eventType === typeFilter) &&
      (statusFilter === "All Status" || r.status === statusFilter.toUpperCase())
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-5 sm:px-5">
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Competitions" }]} />

      <h1 className="mt-3 text-2xl font-extrabold tracking-tight text-ink">Competitions</h1>
      <p className="mt-0.5 text-[13px] text-muted">
        What competitors actually enter — each trophy shot within an event, with its own ranges and its own winners.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<Landmark size={20} />}
          value={new Set(rows.map((r) => r.eventId)).size}
          label="Events"
          sub="Holding them"
          iconBg="bg-success-soft text-success"
        />
        <StatCard icon={<Award size={20} />} value={rows.length} label="Competitions" sub="On record" />
        <StatCard
          icon={<Crosshair size={20} />}
          value={rows.reduce((a, r) => a + r.matches, 0)}
          label="Matches"
          iconBg="bg-violet-50 text-violet-600"
        />
        <StatCard
          icon={<Users size={20} />}
          value={rows.reduce((a, r) => a + r.shooters, 0)}
          label="Entries"
          sub="Across every competition"
          iconBg="bg-warning-soft text-warning"
        />
      </div>

      <div className="card mt-4 flex flex-wrap items-end gap-3 p-3">
        <div className="relative min-w-[220px] flex-1">
          <label className="label">Search</label>
          <SearchIcon size={15} className="pointer-events-none absolute left-3 top-[34px] text-slate-400" />
          <input
            className="input pl-9"
            placeholder="Competition, event, CID or range..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Status</label>
          <Select
            options={["All Status", "Live", "Completed", "Upcoming"]}
            value={statusFilter}
            onChange={setStatusFilter}
            className="w-full sm:w-36"
          />
        </div>
        <div>
          <label className="label">State</label>
          <Select options={["All States", ...states]} value={stateFilter} onChange={setStateFilter} className="w-full sm:w-32" />
        </div>
        <div>
          <label className="label">Event Type</label>
          <Select options={["All Types", ...types]} value={typeFilter} onChange={setTypeFilter} className="w-full sm:w-36" />
        </div>
      </div>

      <div className="card mt-4">
        {loading ? (
          <Loading />
        ) : !filtered.length ? (
          <EmptyState
            icon={Award}
            title="No competitions yet"
            message="Upload a shoot workbook and every competition its Competition sheet declares appears here."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead>
                <tr className="border-b border-line bg-slate-50">
                  <th className="th">Competition</th>
                  <th className="th">Event</th>
                  <th className="th">Type</th>
                  <th className="th">Level</th>
                  <th className="th">Date</th>
                  <th className="th text-center">Clubs</th>
                  <th className="th text-center">Matches</th>
                  <th className="th text-center">Shooters</th>
                  <th className="th text-center">Shots</th>
                  <th className="th">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    onClick={rowClick(() => navigate(link("/competitions/" + c.eventId + "/results")))}
                    className="cursor-pointer transition-colors hover:bg-slate-50"
                  >
                    <td className="td">
                      {/* Straight to the results, because that is what a competition is for. */}
                      <Link to={link("/competitions/" + c.eventId + "/results")} className="font-bold text-ink hover:text-primary">
                        {c.name}
                      </Link>
                      {c.cid && <div className="text-xs text-muted">CID {c.cid}</div>}
                    </td>
                    <td className="td">
                      <Link to={link("/competitions/" + c.eventId)} className="text-slate-600 hover:text-primary">
                        {c.eventName}
                      </Link>
                      <div className="text-xs text-muted">{[c.range, c.stateCode, c.country].filter(Boolean).join(", ")}</div>
                    </td>
                    <td className="td"><EventTypeChip type={c.eventType} /></td>
                    <td className="td text-slate-600">{c.eventLevel || "—"}</td>
                    <td className="td text-slate-600">{dateLabel(c.startDateISO)}</td>
                    <td className="td text-center font-semibold">{c.clubs}</td>
                    <td className="td text-center font-semibold">{c.matches}</td>
                    <td className="td text-center font-semibold">{c.shooters}</td>
                    <td className="td text-center font-semibold">{(c.shots ?? 0).toLocaleString()}</td>
                    <td className="td">
                      <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
